import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Link } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Camera, FileSpreadsheet, Laptop, Unplug, ClockAlert, FileX, ArchiveX, ShieldOff,
  ClipboardCheck, Link2, History, Sparkles, FileCheck2, Gavel, Workflow, Eye,
  Smartphone, FileImage, CheckSquare, FileText, Paperclip, Database, BarChart3, Activity,
  PackageCheck, Stamp, GitBranch, Search, FileBarChart2, ScanLine,
  TrendingDown, Trophy, ShieldCheck, FileSpreadsheet as SpreadsheetIcon,
  ClipboardList, Eye as EyeIcon, DollarSign, Layers,
  Cpu, Boxes, Network, Brain, ShieldHalf, BarChart4, MapPin, Bot,
  ChevronRight,
} from "lucide-react";

// All hero/section imagery is provided by the user — referenced by
// public Emergent customer-asset URLs so the page renders identically
// on preview and the deployed warehousex.primes4.com.
const IMG_HERO = "/images/image-7.webp";
const IMG_PLATFORMS = "/images/image-10.webp";
const IMG_OPERATIONS = "/images/image-9.webp";
const IMG_REPORTING = "/images/image-8.webp";
const IMG_ANALYTICS = "/images/image-6.webp";
// Industry Use Cases — provided by the user.
const IMG_USE_CASES = "/images/image-11.webp";

const HERO_BULLETS = [
  "Mobile-first inspections",
  "SAP-connected evidence & audit trail",
  "Inspection reports & operational visibility",
  "AI-powered insights with PRIVA Assistant",
];

const TODAY_PAINS = [
  { icon: Camera,         label: "Mobile camera photos" },
  { icon: FileSpreadsheet,label: "Excel or paper tracking" },
  { icon: Laptop,         label: "Files stored on laptops" },
  { icon: Unplug,         label: "No SAP linkage" },
  { icon: ClockAlert,     label: "No inspection history" },
  { icon: FileX,          label: "Manual claims tracking" },
  { icon: ArchiveX,       label: "Lost audit evidence" },
  { icon: ShieldOff,      label: "Difficulty proving responsibility" },
];

const WITH_SMARTINSPECT = [
  { icon: ClipboardCheck, label: "Structured digital inspections" },
  { icon: Link2,          label: "SAP-linked evidence" },
  { icon: History,        label: "Central inspection history" },
  { icon: Sparkles,       label: "AI-powered operational insights" },
  { icon: FileCheck2,     label: "Audit-ready traceability" },
  { icon: Gavel,          label: "Faster dispute resolution" },
  { icon: Workflow,       label: "Standardized workflows" },
  { icon: Eye,            label: "Operational visibility" },
];

const ANALYTICS_KPIS = [
  { icon: Sparkles,     title: "AI summaries",            desc: "Automated period-level summaries with key findings." },
  { icon: BarChart3,    title: "Trend analysis",          desc: "Inspection trends, accept/reject rates over time." },
  { icon: Activity,     title: "Rejection insights",      desc: "Top rejection reasons and carrier patterns." },
  { icon: Brain,        title: "Operational intelligence", desc: "Conversational AI assistant on inspection data." },
];

const DELIVERS = [
  { icon: Smartphone,   title: "Mobile inspection capture",   desc: "Capture inspections on phone or tablet from anywhere." },
  { icon: FileImage,    title: "Photo evidence with timestamp", desc: "Before / after photos with location and time stamps." },
  { icon: CheckSquare,  title: "Accept / reject workflows",   desc: "Standardized decisions with defect codes and reasons." },
  { icon: FileText,     title: "Automatic PDF evidence",      desc: "Audit-ready PDF reports generated for every inspection." },
  { icon: Paperclip,    title: "SAP document attachment",     desc: "Evidence attached to SAP delivery, shipment, and EWM docs." },
  { icon: Database,     title: "Central inspection history",  desc: "One source of truth across all sites and carriers." },
  { icon: Sparkles,     title: "AI-powered dashboards",       desc: "AI summaries and conversational insights on demand." },
  { icon: BarChart3,    title: "Operational analytics",       desc: "Trends, KPIs, and carrier performance reporting." },
];

const BENEFITS = [
  { icon: TrendingDown,    label: "Reduce claim payouts" },
  { icon: Trophy,          label: "Improve dispute success" },
  { icon: ShieldCheck,     label: "Increase accountability" },
  { icon: SpreadsheetIcon, label: "Replace Excel tracking" },
  { icon: ClipboardList,   label: "Improve audit readiness" },
  { icon: EyeIcon,         label: "Improve operational visibility" },
  { icon: DollarSign,      label: "Protect revenue" },
  { icon: Layers,          label: "Standardize inspections" },
];

const ROADMAP = [
  { icon: ScanLine, label: "OCR Validation" },
  { icon: Eye,      label: "AI Defect Recognition" },
  { icon: BarChart4, label: "Predictive Risk Trends" },
  { icon: MapPin,   label: "Automated Carrier Scoring" },
  { icon: Bot,      label: "SAP Joule Integration" },
  { icon: Sparkles, label: "GenAI Operational Insights" },
];

// --- Reusable mini components (page-local) ---------------------------------
function SectionHeader({ eyebrow, title, subtitle }) {
  return (
    <div className="max-w-3xl mx-auto text-center mb-10">
      {eyebrow && (
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#025979] mb-3">
          {eyebrow}
        </p>
      )}
      <h2
        className="text-2xl sm:text-3xl font-bold text-[#111827] tracking-tight"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        {title}
      </h2>
      {subtitle && (
        <p className="mt-3 text-sm sm:text-base text-[#6B7280] leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}

function PainCard({ icon: Icon, label, accent = "muted" }) {
  // accent: "muted" → grey/red treatment for the "today" column
  //         "brand" → teal treatment for the "with SmartInspect" column
  const isBrand = accent === "brand";
  return (
    <div
      className={`flex items-start gap-3 p-3.5 rounded-sm border bg-white ${
        isBrand ? "border-[#025979]/30" : "border-[#E5E7EB]"
      }`}
    >
      <div
        className={`w-8 h-8 shrink-0 rounded-sm flex items-center justify-center ${
          isBrand ? "bg-[#E0F2FE] text-[#025979]" : "bg-[#FEF2F2] text-[#B91C1C]"
        }`}
      >
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </div>
      <p className="text-[13px] leading-snug text-[#111827] font-medium">{label}</p>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, desc }) {
  return (
    <Card className="bg-white border border-[#E5E7EB] hover:border-[#025979]/40 hover:shadow-md shadow-sm transition-all duration-200 rounded-sm">
      <CardHeader className="pb-3">
        <div className="w-10 h-10 rounded-sm bg-[#E0F2FE] flex items-center justify-center mb-3">
          <Icon className="w-5 h-5 text-[#025979]" strokeWidth={1.5} />
        </div>
        <CardTitle
          className="text-base font-semibold text-[#111827]"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {title}
        </CardTitle>
        {desc && (
          <CardDescription className="text-[13px] text-[#6B7280] mt-1.5 leading-relaxed">
            {desc}
          </CardDescription>
        )}
      </CardHeader>
    </Card>
  );
}

function openRequestDemo() {
  if (typeof window === "undefined") return;
  // The modal listens on this event and pre-selects from the current
  // pathname — `/smartintegrations/smartinspect` resolves to "smartinspect".
  window.dispatchEvent(new CustomEvent("requestDemo:open", { detail: { solution: "smartinspect" } }));
}

// --- Page ------------------------------------------------------------------
export default function SmartInspectPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1">

        {/* SECTION 1 — HERO ----------------------------------------------- */}
        <section className="bg-[#F8FAFC] border-b border-[#E5E7EB]" data-testid="smartinspect-hero">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <div className="inline-flex items-center gap-1.5 bg-white border border-[#E5E7EB] rounded-sm px-2.5 py-1 mb-4">
                  <Cpu className="w-3.5 h-3.5 text-[#025979]" strokeWidth={1.5} />
                  <span className="text-[11px] font-medium text-[#025979] tracking-wide">
                    WarehouseX SmartIntegrations
                  </span>
                </div>
                <h1
                  className="text-4xl sm:text-5xl font-bold tracking-tight text-[#111827] leading-[1.05]"
                  style={{ fontFamily: "Chivo, sans-serif" }}
                  data-testid="smartinspect-hero-title"
                >
                  WarehouseX SmartInspect
                </h1>
                <p className="mt-4 text-base sm:text-lg text-[#374151] font-medium leading-relaxed max-w-lg">
                  Digital Inspection Management &amp; Operational Intelligence for SAP
                </p>
                <p className="mt-3 text-sm sm:text-base text-[#6B7280] leading-relaxed max-w-lg">
                  Capture inspections, manage evidence, automate reporting, and transform inspection data into actionable operational intelligence across your SAP landscape.
                </p>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4 max-w-lg">
                  {HERO_BULLETS.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-[#111827]">
                      <span className="inline-flex w-4 h-4 rounded-sm bg-[#E0F2FE] text-[#025979] items-center justify-center mt-0.5">
                        <ChevronRight className="w-3 h-3" strokeWidth={2.5} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <Button
                    asChild
                    data-testid="smartinspect-hero-calculate-roi"
                    className="rounded-sm h-11 px-6 text-sm font-semibold bg-[#025979] hover:bg-[#03738C] text-white"
                  >
                    <Link to="/warehousex/smartintegrations/smartinspect/roi">Calculate ROI</Link>
                  </Button>
                  <Button
                    onClick={openRequestDemo}
                    data-testid="smartinspect-hero-request-demo"
                    variant="outline"
                    className="rounded-sm h-11 px-6 text-sm font-semibold border-[#025979] text-[#025979] hover:bg-[#E0F2FE]"
                  >
                    Book Demo
                  </Button>
                </div>
              </div>
              <div>
                <div className="border border-[#E5E7EB] rounded-sm overflow-hidden shadow-sm bg-white">
                  <img loading="lazy"
                    src={IMG_HERO}
                    alt="SmartInspect — AI-Ready Logistics Inspection & Intelligence Platform for SAP"
                    className="w-full h-auto object-contain"
                    data-testid="smartinspect-hero-image"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2 — WHY SMARTINSPECT IS NEEDED -------------------------- */}
        <section className="py-14 md:py-20 bg-white" data-testid="smartinspect-why">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow="The Inspection Gap"
              title="Why Traditional Inspection Processes Are No Longer Enough"
              subtitle="Many organizations still rely on cameras, spreadsheets, shared drives, and manual processes to manage inspections, creating gaps in traceability, evidence management, and business decision-making."
            />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.10em] text-[#B91C1C] mb-3">
                  Today's Challenges
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {TODAY_PAINS.map((p) => (
                    <PainCard key={p.label} {...p} accent="muted" />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.10em] text-[#025979] mb-3">
                  With SmartInspect
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {WITH_SMARTINSPECT.map((p) => (
                    <PainCard key={p.label} {...p} accent="brand" />
                  ))}
                </div>
              </div>
            </div>
            <div
              className="mt-10 rounded-sm border border-[#025979]/30 bg-[#E0F2FE] px-6 py-4 text-sm text-[#0F172A]"
              data-testid="smartinspect-why-highlight"
            >
              Result: Valuable inspection evidence exists, but remains fragmented across devices, folders, emails, spreadsheets, and manual records.
            </div>
          </div>
        </section>

        {/* SECTION 3 — PLATFORM COMPONENTS -------------------------------- */}
        <section className="py-14 md:py-20 bg-[#F8FAFC] border-y border-[#E5E7EB]" data-testid="smartinspect-platforms">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow="SOLUTION OVERVIEW"
              title="SmartInspect Solution Components"
              subtitle="Digital inspection execution, SAP-connected evidence management, and AI-powered operational intelligence working together in a single solution."
            />
            <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden">
              <img loading="lazy"
                src={IMG_PLATFORMS}
                alt="SmartInspect Platform Components — Operations and Analytics"
                className="w-full h-auto"
                data-testid="smartinspect-platforms-image"
              />
            </div>
          </div>
        </section>

        {/* SECTION 4 — OPERATIONS WORKFLOW -------------------------------- */}
        <section className="py-14 md:py-20 bg-white" data-testid="smartinspect-operations">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow="Workflow"
              title="SmartInspect Operations Workflow"
              subtitle="A guided, mobile-first inspection process designed to capture structured inspection data, photo evidence, and operational records directly within SAP-connected business processes."
            />
            <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden">
              <img loading="lazy"
                src={IMG_OPERATIONS}
                alt="SmartInspect Operations Workflow — Document Details, Inspection Codes, Take Photos, Inspection Complete"
                className="w-full h-auto"
                data-testid="smartinspect-operations-image"
              />
            </div>
            <p
              className="mt-6 text-center text-base text-[#475569] max-w-3xl mx-auto"
              data-testid="smartinspect-operations-footer-copy"
            >
              Every inspection is captured digitally, linked to SAP business
              processes, and retained as operational evidence for reporting,
              traceability, and future analysis.
            </p>
          </div>
        </section>

        {/* SECTION 4b — REPORTING, SAP INTEGRATION & AUDIT TRAIL ----------- */}
        <section
          className="py-14 md:py-20 bg-white"
          data-testid="smartinspect-reporting"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow="REPORTING & TRACEABILITY"
              title="Reports, SAP Integration & Audit Trail"
              subtitle="Generate inspection reports, distribute evidence, and retain inspection history directly within SAP business processes."
            />
            <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden">
              <img loading="lazy"
                src={IMG_REPORTING}
                alt="SmartInspect Reporting & Audit Trail — Inspection Completed → Generate Reports → Email & Distribute → Store in SAP → Retrieve Anytime"
                className="w-full h-auto"
                data-testid="smartinspect-reporting-image"
              />
            </div>
            <div
              className="mt-6 text-center max-w-3xl mx-auto"
              data-testid="smartinspect-reporting-footer-copy"
            >
              <p className="text-base font-semibold text-[#0F172A]">
                From Operational Evidence to Audit-Ready Traceability
              </p>
              <p className="mt-2 text-base text-[#475569]">
                Every completed inspection automatically generates structured
                evidence that can be distributed to stakeholders and retained
                directly within SAP business documents.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 5 — ANALYTICS & AI INTELLIGENCE ------------------------ */}
        <section className="py-14 md:py-20 bg-[#F8FAFC] border-y border-[#E5E7EB]" data-testid="smartinspect-analytics">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow="AI & OPERATIONAL INTELLIGENCE"
              title="Turn Inspection Data into Actionable Intelligence"
              subtitle="Transform inspection records, evidence, and operational data into insights that help teams identify trends, understand issues, and take corrective action faster."
            />
            <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden">
              <img loading="lazy"
                src={IMG_ANALYTICS}
                alt="SmartInspect Analytics & AI Intelligence dashboard"
                className="w-full h-auto"
                data-testid="smartinspect-analytics-image"
              />
            </div>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
              {ANALYTICS_KPIS.map((k) => (
                <div
                  key={k.title}
                  className="bg-white border border-[#E5E7EB] rounded-sm p-4 hover:border-[#025979]/40 hover:shadow-sm transition-all"
                  data-testid={`smartinspect-analytics-kpi-${k.title.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="w-8 h-8 rounded-sm bg-[#E0F2FE] flex items-center justify-center mb-3">
                    <k.icon className="w-4 h-4 text-[#025979]" strokeWidth={1.75} />
                  </div>
                  <p className="text-sm font-semibold text-[#111827]">{k.title}</p>
                  <p className="mt-1 text-[12px] text-[#6B7280] leading-relaxed">{k.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 7 — INDUSTRY USE CASES --------------------------------- */}
        <section className="py-14 md:py-20 bg-[#F8FAFC] border-y border-[#E5E7EB]" data-testid="smartinspect-use-cases">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow="Industries"
              title="Industry Use Cases"
              subtitle="Flexible inspection workflows across logistics, warehousing, transportation, manufacturing, and distribution operations."
            />
            <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden">
              <img loading="lazy"
                src={IMG_USE_CASES}
                alt="SmartInspect Industry Use Cases"
                className="w-full h-auto"
                data-testid="smartinspect-use-cases-image"
              />
            </div>
          </div>
        </section>

        {/* SECTION 8 — WHAT SMARTINSPECT DELIVERS ------------------------- */}
        <section className="py-14 md:py-20 bg-white" data-testid="smartinspect-delivers">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow="Capabilities"
              title="What SmartInspect Delivers"
              subtitle="A complete operational evidence platform — from capture to SAP to insight."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {DELIVERS.map((d) => (
                <FeatureCard key={d.title} {...d} />
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 11 — BUSINESS BENEFITS --------------------------------- */}
        <section className="py-14 md:py-20 bg-[#F8FAFC] border-y border-[#E5E7EB]" data-testid="smartinspect-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow="Outcomes"
              title="Business Benefits"
              subtitle="Tangible operational outcomes that move the bottom line."
            />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {BENEFITS.map((b) => (
                <div
                  key={b.label}
                  className="bg-white border border-[#E5E7EB] rounded-sm p-4 flex items-start gap-3 hover:border-[#025979]/40 hover:shadow-sm transition-all"
                >
                  <div className="w-9 h-9 rounded-sm bg-[#E0F2FE] flex items-center justify-center shrink-0">
                    <b.icon className="w-4.5 h-4.5 text-[#025979]" strokeWidth={1.75} />
                  </div>
                  <p className="text-sm font-medium text-[#111827] leading-snug">{b.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 13 — FUTURE ROADMAP ------------------------------------ */}
        <section className="py-14 md:py-20 bg-[#F8FAFC] border-y border-[#E5E7EB]" data-testid="smartinspect-roadmap">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow="Roadmap"
              title="Future Roadmap"
              subtitle="What's coming next on the SmartInspect intelligence layer."
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {ROADMAP.map((r) => (
                <div
                  key={r.label}
                  className="bg-white border border-[#E5E7EB] rounded-sm p-4 flex flex-col items-center text-center hover:border-[#025979]/40 hover:shadow-sm transition-all"
                >
                  <div className="w-9 h-9 rounded-sm bg-[#E0F2FE] flex items-center justify-center mb-3">
                    <r.icon className="w-4.5 h-4.5 text-[#025979]" strokeWidth={1.75} />
                  </div>
                  <p className="text-[12.5px] font-medium text-[#111827] leading-snug">{r.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA ------------------------------------------------------ */}
        <section className="bg-[#025979]" data-testid="smartinspect-final-cta">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 text-center">
            <h2
              className="text-3xl sm:text-4xl font-bold text-white tracking-tight"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              Ready to Modernize Logistics Inspections?
            </h2>
            <p className="mt-4 text-base sm:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
              Deploy SmartInspect to standardize inspections, generate operational evidence, and improve logistics visibility across your SAP landscape.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button
                asChild
                data-testid="smartinspect-final-calculate-roi"
                className="rounded-sm h-11 px-7 text-sm font-semibold bg-white text-[#025979] hover:bg-[#E0F2FE]"
              >
                <Link to="/warehousex/smartintegrations/smartinspect/roi">Calculate ROI</Link>
              </Button>
              <Button
                onClick={openRequestDemo}
                data-testid="smartinspect-final-request-demo"
                variant="outline"
                className="rounded-sm h-11 px-7 text-sm font-semibold border-white text-white hover:bg-white/10"
              >
                Book Demo
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
